import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Container from '../../components/shared/Container';
import { useHeader } from '../../context/HeaderContext';
import { getLocations, submitStoreRequest } from '../../lib/sanity';
import SEO from '../../components/shared/SEO';
import { sortLocationsByDefault } from '../../utils/locationSorting';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

// Available items for ordering
const ORDER_ITEMS = [
  { id: 'burritoStickers', name: 'Burrito Stickers', unit: 'per roll, 100 ea' },
  { id: 'gfStickers', name: 'GF Stickers', unit: 'per roll, 100 ea' },
  { id: 'ojStickers', name: 'OJ Stickers', unit: 'per roll, 100 ea' },
  { id: 'regularCoffeeStickers', name: 'Regular Coffee Stickers', unit: 'per roll, 100 ea' },
  { id: 'decafCoffeeStickers', name: 'Decaf Coffee Stickers', unit: 'per roll, 100 ea' },
  { id: 'kidMenus', name: 'Kid Menus', unit: '1000 ea' },
  { id: 'gmBusinessCards', name: 'GM Business Cards', unit: '50 ea' },
  { id: 'bohHats', name: 'BOH Hats', unit: 'each' },
  { id: 'giftCards', name: 'Gift Cards', unit: 'each' },
  { id: 'academicAchievementAwards', name: 'Academic Achievement Awards', unit: '250 each' },
];

const CORRECT_PASSWORD = 'Pancakes2020';

export default function StoreRequestsPage() {
  const { setUseBlurredBackground } = useHeader();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  // Initialize form data with all items set to 0
  const initialFormData = {
    store: '',
    email: '',
    ...ORDER_ITEMS.reduce((acc, item) => ({ ...acc, [item.id]: 0 }), {})
  };
  const [formData, setFormData] = useState(initialFormData);

  // Enable blurred background for header
  useEffect(() => {
    setUseBlurredBackground(true);
    return () => {
      setUseBlurredBackground(false);
    };
  }, [setUseBlurredBackground]);

  // Fetch locations
  useEffect(() => {
    async function fetchLocations() {
      try {
        const fetchedLocations = await getLocations();
        const sortedData = sortLocationsByDefault(fetchedLocations || []);
        setLocations(sortedData);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchLocations();
  }, []);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Math.max(0, parseInt(value) || 0) : value
    }));
  };

  const handleIncrement = (itemId) => {
    setFormData(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1
    }));
  };

  const handleDecrement = (itemId) => {
    setFormData(prev => ({
      ...prev,
      [itemId]: Math.max(0, (prev[itemId] || 0) - 1)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: '', message: '' });

    // Check if at least one item is ordered
    const hasItems = ORDER_ITEMS.some(item => formData[item.id] > 0);
    if (!hasItems) {
      setFormStatus({
        type: 'error',
        message: 'Please select at least one item to order.'
      });
      setSubmitting(false);
      return;
    }

    try {
      // Get store name for the submission
      const selectedStore = locations.find(loc => loc._id === formData.store);
      
      const result = await submitStoreRequest({
        ...formData,
        storeName: selectedStore?.name || 'Unknown Store'
      });
      
      if (result.success) {
        setFormStatus({
          type: 'success',
          message: 'Your store request has been submitted successfully! A confirmation will be sent to your email.'
        });
        
        // Reset form
        setFormData(initialFormData);
      } else {
        setFormStatus({
          type: 'error',
          message: 'Sorry, there was an error submitting your request. Please try again.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Sorry, there was an error submitting your request. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Password protection screen
  if (!isAuthenticated) {
    return (
      <div className="bg-brand-cream min-h-screen">
        <SEO 
          title="Store Request Form | The Original Pancake House" 
          description="Internal store request form for OPH locations."
          noindex={true}
        />
        
        <motion.div 
          className="bg-brand-navy text-white pt-32 pb-12"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <Container>
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-heading mb-4">Store Request Form</h1>
              <p className="text-xl text-white text-opacity-90 max-w-2xl mx-auto">
                Internal use only - Please enter the password to continue
              </p>
            </div>
          </Container>
        </motion.div>

        <section className="py-16 bg-white">
          <Container>
            <motion.div
              variants={sectionVariants}
              initial="hidden"
              animate="visible"
              className="max-w-md mx-auto"
            >
              <motion.form variants={itemVariants} onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-brand-navy mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy"
                    placeholder="Enter password"
                    autoFocus
                    autoComplete="off"
                  />
                </div>

                {passwordError && (
                  <div className="p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">
                    {passwordError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-brand-navy text-white py-4 px-6 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                >
                  Access Form
                </button>
              </motion.form>
            </motion.div>
          </Container>
        </section>
      </div>
    );
  }

  // Main form
  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO 
        title="Store Request Form | The Original Pancake House" 
        description="Internal store request form for OPH locations."
        noindex={true}
      />
      
      {/* Hero Section */}
      <motion.div 
        className="bg-brand-navy text-white pt-32 pb-12"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <Container>
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-heading mb-4">Store Request Form</h1>
            <p className="text-xl text-white text-opacity-90 max-w-2xl mx-auto">
              Order supplies for your store location
            </p>
          </div>
        </Container>
      </motion.div>

      {/* Form Section */}
      <section className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-8">
              {/* Store Selection */}
              <div className="bg-brand-cream p-6 rounded-lg">
                <h2 className="text-2xl font-heading text-brand-navy mb-4">Store Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="store" className="block text-sm font-medium text-brand-navy mb-2">
                      Select Store *
                    </label>
                    <select
                      id="store"
                      name="store"
                      value={formData.store}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy bg-white"
                    >
                      <option value="">Select your store</option>
                      {locations.map((location) => (
                        <option key={location._id} value={location._id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-brand-navy mb-2">
                      Confirmation Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-brand-cream p-6 rounded-lg">
                <h2 className="text-2xl font-heading text-brand-navy mb-4">Order Items</h2>
                <p className="text-gray-600 mb-6">Select the quantity needed for each item</p>
                
                <div className="space-y-4">
                  {ORDER_ITEMS.map((item) => (
                    <div 
                      key={item.id}
                      className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <h3 className="font-medium text-brand-navy">{item.name}</h3>
                        <p className="text-sm text-gray-500">{item.unit}</p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => handleDecrement(item.id)}
                          className="w-10 h-10 rounded-full bg-brand-navy text-white flex items-center justify-center hover:bg-opacity-90 transition-colors disabled:opacity-50"
                          disabled={formData[item.id] === 0}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        <input
                          type="number"
                          name={item.id}
                          value={formData[item.id]}
                          onChange={handleChange}
                          min="0"
                          className="w-16 text-center px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy"
                        />
                        
                        <button
                          type="button"
                          onClick={() => handleIncrement(item.id)}
                          className="w-10 h-10 rounded-full bg-brand-navy text-white flex items-center justify-center hover:bg-opacity-90 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              {ORDER_ITEMS.some(item => formData[item.id] > 0) && (
                <div className="bg-white border-2 border-brand-navy p-6 rounded-lg">
                  <h2 className="text-2xl font-heading text-brand-navy mb-4">Order Summary</h2>
                  <ul className="space-y-2">
                    {ORDER_ITEMS.filter(item => formData[item.id] > 0).map((item) => (
                      <li key={item.id} className="flex justify-between text-gray-700">
                        <span>{item.name}</span>
                        <span className="font-medium">{formData[item.id]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Status Message */}
              {formStatus.message && (
                <div className={`p-4 rounded-lg ${
                  formStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {formStatus.message}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || loading}
                className="w-full bg-brand-navy text-white py-4 px-6 rounded-lg font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              >
                {submitting ? 'Submitting Request...' : 'Submit Store Request'}
              </button>
            </motion.form>
          </motion.div>
        </Container>
      </section>
    </div>
  );
}

